import { Language } from '../context/LanguageContext';

// Import fallback static content
import { homeTemplateTranslations } from '../translations/homeTemplate';

// Environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const ENABLE_API = process.env.REACT_APP_ENABLE_API === 'true';

// API request timeout in ms
const REQUEST_TIMEOUT = 5000;

// Create a webpack context for all JSON article files
const articlesContext = (require as any).context('../data/articles', true, /\.json$/);

/**
 * Create a promise that rejects after a timeout
 */
const timeoutPromise = (timeout: number) => {
    return new Promise((_, reject) => {
        setTimeout(() => {
            reject(new Error(`Request timed out after ${timeout}ms`));
        }, timeout);
    });
};

/**
 * Loads local JSON content in production mode
 */
const loadLocalContent = async (page: string, language: Language): Promise<any> => {
    const contextPath = `./${language}/${page}-content.json`;
    try {
        // Load JSON content from webpack context
        const module = articlesContext(contextPath);
        // JSON modules export content as default; fallback to module itself
        const data = (module as any).default ?? module;
        return data;
    } catch (error) {
        console.error(`Error loading local content from ${contextPath}:`, error);
        // Fallback to static translations
        if (page === 'home') {
            return homeTemplateTranslations[language];
        }
        return null;
    }
};

/**
 * Fetches content for a specific page and language
 */
export const fetchContent = async (page: string, language: Language) => {
    // If API is disabled (production mode), use local JSON content
    if (!ENABLE_API) {
        console.log(`API disabled, using local content for ${page} (${language})`);
        return loadLocalContent(page, language);
    }

    try {
        // Add timeout to the fetch request
        const response = await Promise.race([
            fetch(`${API_BASE_URL}/content/${language}/${page}`),
            timeoutPromise(REQUEST_TIMEOUT)
        ]) as Response;

        if (!response.ok) {
            throw new Error(`Failed to fetch content: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error(`Error fetching ${page} content for ${language}:`, error);

        // Fallback to static content on error
        if (page === 'home') {
            console.log(`Falling back to static content for ${page} (${language})`);
            return homeTemplateTranslations[language];
        }

        return null;
    }
};

/**
 * Saves content updates for a specific page and language
 */
export const saveContent = async (page: string, language: Language, content: any): Promise<boolean> => {
    if (!ENABLE_API) {
        console.warn('API is disabled, content not saved');
        return false;
    }

    try {
        // Add timeout to the fetch request
        const response = await Promise.race([
            fetch(`${API_BASE_URL}/content/${language}/${page}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(content),
            }),
            timeoutPromise(REQUEST_TIMEOUT)
        ]) as Response;

        if (!response.ok) {
            throw new Error(`Failed to save content: ${response.statusText}`);
        }

        const data = await response.json();
        return data.success === true;
    } catch (error) {
        console.error(`Error saving ${page} content for ${language}:`, error);
        return false;
    }
};

/**
 * Downloads watch card certificate
 *
 * Flow:
 * 1. For dealer preview (with certificateData): generates PDF on-the-fly
 * 2. For customer download (no certificateData): gets presigned URL from S3
 */
type DownloadWatchCardOptions = {
    certificateData?: Record<string, any>;
};

export const downloadWatchCard = async (
    serial: string,
    pin: string,
    options?: DownloadWatchCardOptions
): Promise<{ success: boolean; blob?: Blob; filename?: string; response?: Response; url?: string }> => {
    try {
        // If certificateData is provided, generate PDF on-the-fly (dealer preview)
        if (options?.certificateData) {
            const payload: Record<string, any> = { serial, pin, certificateData: options.certificateData };

            const response = await Promise.race([
                fetch(`${API_BASE_URL}/certificates`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/pdf',
                    },
                    body: JSON.stringify(payload),
                }),
                timeoutPromise(REQUEST_TIMEOUT * 2) // Allow extra time for PDF generation
            ]) as Response;

            if (!response.ok) {
                return { success: false, response };
            }

            const blob = await response.blob();
            const filename = `watch-card-${serial}.pdf`;

            return { success: true, blob, filename };
        }

        // Otherwise, get presigned URL from S3 (customer download)
        const response = await Promise.race([
            fetch(`${API_BASE_URL}/certificates/download`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ serial, pin }),
            }),
            timeoutPromise(REQUEST_TIMEOUT)
        ]) as Response;

        if (!response.ok) {
            return { success: false, response };
        }

        const data = await response.json();

        if (!data.success || !data.url) {
            return { success: false };
        }

        // Return the presigned URL for download
        return { success: true, url: data.url, filename: `watch-card-${serial}.pdf` };

    } catch (error) {
        console.error('Error downloading watch card:', error);
        return { success: false };
    }
}; 
